import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Metafield-driven product image carousel: thumbnails swap the main image; full replacement on variant update.
 *
 * @typedef {object} Refs
 * @property {HTMLImageElement} [mainImage] - Main product image.
 *
 * @extends {Component<Refs>}
 */
export class VariantMetafieldCarousel extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.addEventListener('click', this.#onClick);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * @param {Event} event
   */
  #handleVariantUpdate = (event) => {
    const source = /** @type {CustomEvent} */ (event).detail?.data?.html;
    if (!source) return;

    const next = source.querySelector('variant-metafield-carousel');
    if (!next) return;

    this.replaceWith(next);
  };

  /**
   * @param {Event} event
   */
  #onClick = (event) => {
    const btn = event.target.closest('[data-carousel-thumb]');
    if (!(btn instanceof HTMLButtonElement)) return;
    this.#selectThumb(btn);
  };

  /**
   * @param {HTMLButtonElement} btn
   */
  #selectThumb(btn) {
    const img = this.refs.mainImage;
    if (!(img instanceof HTMLImageElement)) return;

    const { mainSrc, mainSrcset, mainSizes, alt } = btn.dataset;

    if (mainSrc) {
      img.src = mainSrc;
    }

    if (mainSrcset) {
      img.srcset = mainSrcset;
    } else {
      img.removeAttribute('srcset');
    }

    if (mainSizes) {
      img.sizes = mainSizes;
    } else {
      img.removeAttribute('sizes');
    }

    if (alt !== undefined) {
      img.alt = alt;
    }

    const thumbs = this.querySelectorAll('[data-carousel-thumb]');
    thumbs.forEach((el) => {
      if (!(el instanceof HTMLButtonElement)) return;
      const selected = el === btn;
      el.classList.toggle('is-selected', selected);
      el.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
  }
}

if (!customElements.get('variant-metafield-carousel')) {
  customElements.define('variant-metafield-carousel', VariantMetafieldCarousel);
}
